import { motion } from "framer-motion";
import { Brush, RadioTower, Trophy, Tv } from "lucide-react";

import { Card, LinkButton, SectionHeader } from "./ui";

const principles = [
  {
    title: "Broadcast Taste",
    body: "The details matter: hierarchy, contrast, timing, spacing, sponsor visibility, and motion that feels intentional instead of pasted on.",
    icon: Tv,
  },
  {
    title: "Creative Tools",
    body: "iBox gives streamers real control, but the controls are shaped by design judgment so the output still feels polished and readable.",
    icon: Brush,
  },
  {
    title: "Streamer Built",
    body: "The product comes from the same workflows it serves: configuring sources, testing layouts, going live, spotting problems, and fixing them before the next stream.",
    icon: RadioTower,
  },
  {
    title: "Race Proven",
    body: "The first iBox tools were used for years on sim-racing streams before becoming a product other creators could download.",
    icon: Trophy,
  },
];

export function AboutPage() {
  return (
    <main className="relative min-h-screen overflow-hidden pt-16">
      <div className="download-bg absolute inset-0 z-0 bg-bg">
        <div className="absolute inset-0 bg-gt-grid opacity-35" />
        <div className="absolute inset-0 bg-bg/38" />
        <div className="pointer-events-none absolute inset-x-0 bottom-0 h-64 bg-gradient-to-b from-transparent to-bg" />
      </div>

      <section className="relative z-10 mx-auto max-w-7xl px-6 py-24">
        <div className="grid gap-12 lg:grid-cols-[0.9fr_1.1fr] lg:items-end">
          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.45, ease: "easeOut" }}
          >
            <div className="mb-6 flex items-center gap-3">
              <div className="h-px w-10 bg-primary" />
              <span className="text-[11px] font-semibold uppercase tracking-[0.3em] text-primary">About iBox</span>
            </div>
            <h1 className="font-display text-6xl font-bold uppercase leading-[0.9] text-text md:text-8xl">Made By Streamers.</h1>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.45, delay: 0.08, ease: "easeOut" }}
            className="max-w-2xl lg:justify-self-end"
          >
            <p className="text-lg leading-relaxed text-muted md:text-xl">
              iBox is a collection of streaming tools created by streamers for streamers: practical broadcast apps built around the way creators actually go live.
            </p>
            <p className="mt-5 text-base leading-relaxed text-muted">
              It started with one problem: sim-racing streams had plenty of timing overlays, but not enough ways to tell the larger story of a broadcast. Sponsors, league updates, socials, headlines, stats, and channel context all needed a better place to live.
            </p>
          </motion.div>
        </div>
      </section>

      <section className="relative z-10 mx-auto max-w-7xl px-6 pb-24">
        <div className="rounded-xl border border-border bg-surface/92 p-6 shadow-2xl shadow-black/20 md:p-8">
          <div className="mb-6 flex items-center gap-3">
            <div className="h-px w-10 bg-primary" />
            <span className="text-[11px] font-semibold uppercase tracking-[0.3em] text-primary">The Origin</span>
          </div>
          <div className="grid gap-8 lg:grid-cols-[1fr_0.8fr]">
            <div className="space-y-4 text-sm leading-7 text-muted md:text-base">
              <p>
                Before iBox was a product, it was a private Python tool running beside iRacing and broadcast software. It powered real stream graphics for ChampDrivesCars, where the only test that mattered was whether the overlay looked good, stayed readable, and survived a live race.
              </p>
              <p>
                That early version worked, but it was configured through code. It could do the job for one person, not for every streamer who asked how to get the same ticker on their own channel.
              </p>
              <p>
                iBox exists to turn that private workflow into something visual, reliable, and shareable: a desktop app where creators can choose the apps they need, configure them without code, and build broadcast graphics that feel like part of the show.
              </p>
            </div>
            <h2 className="font-display text-4xl font-bold uppercase leading-none text-text md:text-5xl">
              iBox came from years of making streams work the hard way.
            </h2>
          </div>
        </div>
      </section>

      <section className="relative z-10 mx-auto max-w-7xl px-6 pb-24">
        <SectionHeader
          eyebrow="Why This Exists"
          title="The Stream Is The Product"
          description="iBox is built by people who know the pressure of going live, not just the theory of making overlays."
        />

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          {principles.map((principle, index) => (
            <motion.div
              key={principle.title}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.35, delay: index * 0.05 }}
            >
              <Card className="h-full" interactive={false}>
                <div className="mb-5 flex h-12 w-12 items-center justify-center rounded-lg border border-border bg-bg text-primary">
                  <principle.icon size={22} strokeWidth={1.6} />
                </div>
                <h3 className="font-display text-2xl font-bold uppercase leading-none text-text">{principle.title}</h3>
                <p className="mt-4 text-sm leading-7 text-muted">{principle.body}</p>
              </Card>
            </motion.div>
          ))}
        </div>
      </section>

      <section className="relative z-10 mx-auto max-w-7xl px-6 pb-24">
        <div className="rounded-xl border border-border bg-surface/92 p-6 md:p-8">
          <div className="grid gap-8 lg:grid-cols-[0.82fr_1fr] lg:items-center">
            <div>
              <span className="text-[11px] font-semibold uppercase tracking-[0.3em] text-primary">Founder Perspective</span>
              <h2 className="mt-5 font-display text-4xl font-bold uppercase leading-none text-text md:text-5xl">
                Broadcast craft, sim-racing obsession, and a lot of OBS scars.
              </h2>
            </div>
            <div className="space-y-4 text-sm leading-7 text-muted md:text-base">
              <p>
                iBox was created by Chris James Champeau, a Los Angeles-based director, editor, and visual effects artist with a background across film, television, commercials, music videos, and post-production. That visual background is the reason iBox treats design as part of the product, not just a coat of paint.
              </p>
              <p>
                Chris also streams and races as ChampDrivesCars, which is where the first iBox-style ticker tools were tested for years. The result is software shaped by both sides of the problem: the creative discipline of broadcast graphics and the practical reality of running a stream while racing.
              </p>
              <div className="flex flex-col gap-3 border-t border-border pt-5 sm:flex-row">
                <LinkButton href="https://champdrivescars.com" target="_blank" rel="noreferrer" variant="ghost" className="flex-1">
                  Champ Drives Cars
                </LinkButton>
                <LinkButton href="/contact" className="flex-1">
                  Contact iBox
                </LinkButton>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="relative z-10 mx-auto max-w-7xl px-6 pb-24">
        <div className="grid gap-5 lg:grid-cols-3">
          <Card interactive={false} className="lg:col-span-2">
            <span className="text-[11px] font-semibold uppercase tracking-[0.3em] text-primary">What Comes Next</span>
            <h2 className="mt-5 font-display text-4xl font-bold uppercase leading-none text-text md:text-5xl">
              One app first. A toolkit after that.
            </h2>
            <p className="mt-5 text-sm leading-7 text-muted md:text-base">
              The Broadcast Ticker is the flagship because it solves a real, visible problem first. From there, iBox can grow into a wider collection of streaming apps: tools for overlays, viewer interaction, race context, production control, and whatever else makes live streams feel more produced without making creators feel buried.
            </p>
          </Card>

          <Card interactive={false}>
            <span className="text-[11px] font-semibold uppercase tracking-[0.3em] text-primary">Start Here</span>
            <h3 className="mt-5 font-display text-3xl font-bold uppercase leading-none text-text">Broadcast Ticker</h3>
            <p className="mt-4 text-sm leading-7 text-muted">
              The first iBox app is a modular information rail for sponsors, socials, headlines, league updates, stats, and stream context.
            </p>
            <div className="mt-6 border-t border-border pt-5">
              <LinkButton href="/download" className="w-full">
                Get iBox
              </LinkButton>
            </div>
          </Card>
        </div>
      </section>
    </main>
  );
}
